import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MapPin, ChatCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [price, setPrice] = useState(0);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data));
  }, [id]);

  const submitProposal = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/jobs/${id}/proposals`, { price: parseFloat(price), message });
      toast.success("Proposal sent");
      const { data } = await api.get(`/jobs/${id}`);
      setJob(data);
      setMessage("");
    } catch (e) {
      toast.error("Could not submit proposal");
    } finally {
      setBusy(false);
    }
  };

  const messageCustomer = async () => {
    if (!job) return;
    try {
      const { data } = await api.post("/conversations", { other_user_id: job.customer_id });
      navigate(`/chat/${data.conversation_id}`);
    } catch (e) {
      toast.error("Could not start chat");
    }
  };

  const messageWorker = async (worker_id) => {
    try {
      const { data } = await api.post("/conversations", { other_user_id: worker_id });
      navigate(`/chat/${data.conversation_id}`);
    } catch (e) {
      toast.error("Could not start chat");
    }
  };

  if (!job) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

  const isWorker = user?.role === "worker";
  const isOwner = user?.user_id === job.customer_id;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-24 pb-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">{job.category}</div>
        <h1 className="font-display text-4xl mb-4">{job.title}</h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-6">
          <span className="flex items-center gap-1"><MapPin size={14} /> {job.city}, {job.province}</span>
          <span className="font-semibold text-slate-900">${job.budget_min}–${job.budget_max} CAD</span>
          <span className="text-[10px] uppercase tracking-wider bg-slate-100 px-2 py-0.5">{job.status}</span>
        </div>
        <p className="text-base text-slate-700 leading-relaxed whitespace-pre-line mb-10">{job.description}</p>

        {isWorker && (
          <section className="border border-slate-200 p-6 mb-10">
            <h2 className="font-display text-2xl mb-4">Send a proposal</h2>
            <form onSubmit={submitProposal} className="space-y-3">
              <input required type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Your price (CAD)" className="w-full border border-slate-300 px-3 py-2.5" data-testid="proposal-price" />
              <textarea required rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell the customer how you'll do it…" className="w-full border border-slate-300 px-3 py-2.5" data-testid="proposal-message" />
              <button type="submit" disabled={busy} className="bg-brand text-white px-5 py-2.5 font-semibold disabled:opacity-50" data-testid="submit-proposal-btn">
                {busy ? "Sending…" : "Submit proposal"}
              </button>
              <button type="button" onClick={messageCustomer} className="ml-2 bg-white border border-slate-300 px-5 py-2.5 font-semibold text-sm" data-testid="message-customer-btn">
                <ChatCircle size={14} className="inline mr-1" /> Message customer
              </button>
            </form>
          </section>
        )}

        {isOwner && (
          <section>
            <h2 className="font-display text-2xl mb-4">Proposals ({job.proposals?.length || 0})</h2>
            {job.proposals?.length === 0 ? (
              <p className="text-slate-500">No proposals yet.</p>
            ) : (
              <div className="space-y-3">
                {job.proposals.map((p) => (
                  <div key={p.proposal_id} className="border border-slate-200 p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold">{p.worker_name}</div>
                      <div className="font-display text-xl">${p.price}</div>
                    </div>
                    <p className="text-sm text-slate-700 mb-3">{p.message}</p>
                    <button onClick={() => messageWorker(p.worker_id)} className="bg-slate-900 text-white px-4 py-2 text-sm font-semibold" data-testid={`chat-with-${p.worker_id}`}>
                      <ChatCircle size={14} className="inline mr-1" /> Message
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
      <Footer />
    </div>
  );
}
